/**
 * Contact import for browsers without the Contact Picker API.
 *
 * Safari — including iOS and an iOS installed PWA — does not implement
 * `navigator.contacts` at all, and no web page can read an address book. The
 * one thing an iOS user can do from the Contacts app is share/export a contact
 * as a vCard (.vcf), so we accept that file (or a pasted vCard / loose text
 * line) and normalize it into the same shape the platform pickers produce.
 */

const VCARD_LINE_BREAK = /\r\n|\r|\n/

/** vCard 3.0/4.0 escaped characters (RFC 6350 §3.4). */
function unescapeValue(value) {
  return String(value ?? "")
    .replace(/\\n/gi, " ")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\")
    .trim()
}

/** RFC 6350 §3.2 line folding: a continuation starts with a space or tab. */
function unfold(text) {
  return String(text ?? "")
    .split(VCARD_LINE_BREAK)
    .reduce((lines, line) => {
      if (/^[ \t]/.test(line) && lines.length) {
        lines[lines.length - 1] += line.slice(1)
        return lines
      }
      lines.push(line)
      return lines
    }, [])
}

/** Splits `TEL;TYPE=CELL;VALUE=uri:tel:+225...` into key, params and value. */
function parseProperty(line) {
  let index = 0
  let inQuotes = false
  while (index < line.length) {
    const char = line[index]
    if (char === '"') inQuotes = !inQuotes
    else if (char === ":" && !inQuotes) break
    index += 1
  }
  const descriptor = line.slice(0, index)
  const value = line.slice(index + 1)
  const [rawKey, ...paramParts] = descriptor.split(";")
  // `item1.TEL` and `TEL;...` both mean TEL.
  const key = rawKey.split(".").pop().trim().toUpperCase()
  const params = paramParts
    .map((part) => {
      const eq = part.indexOf("=")
      if (eq === -1) return { key: part.trim().toUpperCase(), value: "" }
      return { key: part.slice(0, eq).trim().toUpperCase(), value: part.slice(eq + 1).trim().replace(/^"|"$/g, "") }
    })
    .filter((p) => p.key)
  return { key, params, value: unescapeValue(value) }
}

const isMobile = (params) =>
  params.some((p) => p.value.split(",").some((v) => /^(CELL|MOBILE)$/i.test(v.trim())))

function cleanPhone(value) {
  const trimmed = String(value ?? "").trim()
  if (!trimmed) return ""
  // `tel:` / `sip:` URIs
  const uri = /^(tel|sip):/i.test(trimmed) ? trimmed.replace(/^(tel|sip):/i, "") : trimmed
  // Keep a leading +, drop the rest of the separators.
  const cleaned = uri.trim().replace(/[^\d+]/g, "")
  return /^\+?[\d\s().-]{3,}$/.test(uri.trim()) ? cleaned : ""
}

/**
 * Parses one or many vCards. Returns app-shaped contacts, matching what
 * `toAppContact()` produces for the native and web pickers.
 */
export function parseVCards(text) {
  const lines = unfold(text)
  const cards = []
  let current = null

  lines.forEach((rawLine) => {
    const line = rawLine.trim()
    if (!line) return
    const { key, params, value } = parseProperty(line)

    if (key === "BEGIN") {
      if (value.toUpperCase() === "VCARD") {
        current = { phones: [], emails: [], photos: [] }
      }
      return
    }
    if (key === "END") {
      if (current && value.toUpperCase() === "VCARD") {
        cards.push(current)
        current = null
      }
      return
    }
    if (!current) return

    switch (key) {
      case "FN":
        current.displayName = current.displayName || value
        break
      case "N": {
        // family;given;additional;prefix;suffix
        const [family = "", given = "", additional = ""] = value.split(";")
        current.name = {
          familyName: family.trim(),
          givenName: [given, additional].map((p) => p.trim()).filter(Boolean).join(" "),
        }
        break
      }
      case "TEL": {
        const number = cleanPhone(value)
        if (number) current.phones.push({ number, mobile: isMobile(params) })
        break
      }
      case "EMAIL": {
        const address = value.trim()
        if (address && address.includes("@")) current.emails.push({ address })
        break
      }
      case "PHOTO":
        current.photos.push({ value })
        break
      default:
        break
    }
  })

  return cards
    .map((card) => {
      const name = card.name || { givenName: "", familyName: "" }
      const displayName =
        card.displayName ||
        [name.givenName, name.familyName].map((p) => p.trim()).filter(Boolean).join(" ")
      return {
        id: undefined,
        displayName,
        name: {
          givenName: name.givenName.trim(),
          familyName: name.familyName.trim(),
        },
        phones: card.phones,
        emails: card.emails,
        photos: card.photos,
      }
    })
    .filter((contact) => contact.displayName || contact.phones.length || contact.emails.length)
}

const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i
const PHONE_RE = /(?:\+?\d[\d\s().-]{5,}\d)/

/**
 * Fallback for pasted text: "Aya Kone, +225 07 01 23 45 67, aya@example.com".
 * Used when the user pastes a contact from another app instead of a vCard.
 */
export function parseContactText(text) {
  const source = String(text ?? "").trim()
  if (!source) return null

  if (/BEGIN:VCARD/i.test(source)) {
    const [first] = parseVCards(source)
    return first || null
  }

  const emailMatch = source.match(EMAIL_RE)
  const email = emailMatch ? emailMatch[0] : ""
  const withoutEmail = email ? source.replace(emailMatch[0], " ") : source
  const phoneMatch = withoutEmail.match(PHONE_RE)
  const number = phoneMatch ? cleanPhone(phoneMatch[0]) : ""
  const withoutPhone = phoneMatch ? withoutEmail.replace(phoneMatch[0], " ") : withoutEmail
  const name = withoutPhone
    .replace(/[;,|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()

  if (!name && !number && !email) return null

  const words = name.split(" ").filter(Boolean)
  return {
    id: undefined,
    displayName: name,
    name: {
      // A one-word name ("Prince") has no family part; keeping it in givenName
      // stops it from being dropped entirely.
      givenName: words.length > 1 ? words.slice(0, -1).join(" ") : words[0] || "",
      familyName: words.length > 1 ? words[words.length - 1] : "",
    },
    // A pasted local number is still useful, but it must not be treated as
    // international: `applyPhoneNumber` only prefixes "+" when present.
    phones: number ? [{ number, mobile: false }] : [],
    emails: email ? [{ address: email }] : [],
    photos: [],
  }
}

/** Single entry point: accepts a vCard file or pasted text. */
export function parseContact(text) {
  return parseContactText(text)
}

/** Reads a picked .vcf File into text (UTF-8, with a latin1 retry). */
export async function readContactFile(file) {
  const buffer = await file.arrayBuffer()
  try {
    return new TextDecoder("utf-8").decode(buffer)
  } catch {
    return new TextDecoder("latin1").decode(buffer)
  }
}
